import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Box, Modal, FormControl, OutlinedInput, styled, TablePagination, Autocomplete, TextField, FormControlLabel, Radio, RadioGroup, Checkbox } from "@mui/material";
import { Add, CloseOutlined, ArrowBack } from "@mui/icons-material";
import toast from "react-hot-toast";

import useAuthFetch from "../../hooks/useAuthFetch";
import CustomTable from "../table/CustomTable";
import CustomTableRow from "../table/CustomTableRow";
import TableHeader from "../table/TableHeader";
import MainTableBody from "../table/MainTableBody";
import { LoadingTableSkeleton } from "../../utils/common";
import NoDataTable from "../table/NoDataTable";
import ConfirmBox from "../ConfirmBox";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterDropdown from "../filter/FilterDropdown";

const CustomizeTextField = styled(OutlinedInput)`
  & .MuiOutlinedInput-notchedOutline {
    border: none;
  }
`;

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 600,
    bgcolor: "background.paper",
    boxShadow: 24,
    padding: 4,
    borderRadius: 2,
};

const QnaModal = ({ open, handleClose, refetch, initialData, templateId, isEdit, availableLanguages = [], defaultLanguage, onLanguageChange }) => {
    const customFetch = useAuthFetch();
    const [question, setQuestion] = useState("");
    const [language, setLanguage] = useState(defaultLanguage || "English");
    const [answersStr, setAnswersStr] = useState("YES || NO");
    const [productCodeType, setProductCodeType] = useState("");
    const [exactProductCode, setExactProductCode] = useState("");
    const [productCodeStartingWith, setProductCodeStartingWith] = useState([]);
    const [isNonNoType, setIsNonNoType] = useState(false);
    const [terminationAnswer, setTerminationAnswer] = useState("");
    const [errors, setErrors] = useState({});

    const productCodeStartingWithOptions = ["A", "D", "E", "G", "I", "M", "T", "U", "X", "Others"];

    const allLanguages = React.useMemo(() => {
        return availableLanguages
    }, [availableLanguages]);

    useEffect(() => {
        if (open) {
            if (isEdit && initialData) {
                setQuestion(initialData.question);
                setLanguage(initialData.language || "English");
                setAnswersStr(Array.isArray(initialData.answers) ? initialData.answers.join(" || ") : initialData.answers || "");
                setProductCodeType(initialData.productCodeType || "");
                setExactProductCode(initialData.productCodeType === "EXACT" ? (initialData.productCodeValue || "") : "");
                setProductCodeStartingWith(initialData.productCodeType === "STARTING_WITH" ? (initialData.productCodeValue?.split(',') || []) : []);
                setIsNonNoType(initialData.isNonNoType || false);
                setTerminationAnswer(initialData.terminationAnswer || "");
            } else {
                setQuestion("");
                setLanguage(defaultLanguage || "English");
                setAnswersStr("YES || NO");
                setProductCodeType("");
                setExactProductCode("");
                setProductCodeStartingWith([]);
                setIsNonNoType(false);
                setTerminationAnswer("");
            }
            setErrors({});
        }
    }, [open, isEdit, initialData]);

    const handleProductCodeTypeChange = (event) => {
        setProductCodeType(event.target.value);
        setExactProductCode("");
        setProductCodeStartingWith([]);
        setErrors({});
    };

    const handleExactProductCodeChange = (event) => {
        setExactProductCode(event.target.value.toUpperCase());
        // Clear error when user starts typing
        if (errors.exactProductCode) {
            setErrors(prev => ({ ...prev, exactProductCode: "" }));
        }
    };

    const handleProductCodeStartingWithChange = (char) => {
        setProductCodeStartingWith(prev => {
            if (prev.includes(char)) {
                return prev.filter(c => c !== char);
            } else {
                return [...prev, char];
            }
        });
        // Clear error when user changes selection
        if (errors.productCodeStartingWith) {
            setErrors(prev => ({ ...prev, productCodeStartingWith: "" }));
        }
    };

    const handleSubmit = async () => {
        const newErrors = {};

        if (!question) {
            newErrors.question = "Question is required";
        }
        if (!language) {
            newErrors.language = "Language is required";
        }

        if (productCodeType === "EXACT" && !exactProductCode?.trim()) {
            newErrors.exactProductCode = "Exact Product Code is required";
        } else if (productCodeType === "EXACT" && !/^[A-Z0-9]{1,10}$/.test(exactProductCode.trim().toUpperCase())) {
            newErrors.exactProductCode = "Product Code must be alphanumeric and 1-10 characters";
        }

        if (productCodeType === "STARTING_WITH" && (!productCodeStartingWith || productCodeStartingWith.length === 0)) {
            newErrors.productCodeStartingWith = "At least one Product Code Starting With option must be selected";
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            if (newErrors.question) toast.error(newErrors.question);
            if (newErrors.language) toast.error(newErrors.language);
            if (newErrors.exactProductCode) toast.error(newErrors.exactProductCode);
            if (newErrors.productCodeStartingWith) toast.error(newErrors.productCodeStartingWith);
            return;
        }

        const answersArray = answersStr.split("||").map(s => s.trim()).filter(Boolean);

        const url = new URL(
            `${process.env.REACT_APP_API_URL}/v1/dashboard/qnaAssociation/${isEdit ? "update" : "create"}`
        );

        const body = {
            templateId,
            question,
            answers: answersArray,
            language,
            productCodeType: productCodeType || null,
            productCodeValue: productCodeType
                ? (productCodeType === "EXACT"
                    ? exactProductCode.trim().toUpperCase()
                    : productCodeStartingWith.join(','))
                : null,
            isNonNoType: isNonNoType || false,
            terminationAnswer: isNonNoType ? terminationAnswer : null,
            ...(isEdit && { id: initialData.id })
        };

        const { response, status, message } = await customFetch(url, "POST", body);

        if (status === 200 || status === 201) {
            toast.success(`QnA ${isEdit ? "Updated" : "Created"} Successfully!`);
            if (!isEdit && onLanguageChange) {
                onLanguageChange(language);
            }
            handleClose();
            refetch();
        } else {
            toast.error(message || "Something went wrong!");
        }
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <Box sx={style}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">{isEdit ? "Edit" : "Create"} Question</h2>
                    <button onClick={handleClose}><CloseOutlined /></button>
                </div>

                <div className="space-y-4">
                    <FormControl fullWidth>
                        <label className="mb-1 text-sm font-semibold">Question</label>
                        <CustomizeTextField
                            placeholder="Enter Question"
                            className="bg-gray-100 rounded-lg"
                            multiline
                            rows={3}
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                        />
                    </FormControl>

                    <FormControl fullWidth>
                        <label className="mb-1 text-sm font-semibold">Product Code</label>
                        <RadioGroup
                            row
                            aria-labelledby="product-code-type-radio-group"
                            name="product-code-type-radio-group"
                            value={productCodeType}
                            onChange={handleProductCodeTypeChange}
                            className="mb-2"
                        >
                            <FormControlLabel
                                value="EXACT"
                                control={<Radio />}
                                label="Exact Product Code"
                            />
                            <FormControlLabel
                                value="STARTING_WITH"
                                control={<Radio />}
                                label="Product Code Starting With"
                            />
                        </RadioGroup>

                        {productCodeType === "EXACT" && (
                            <div className="mt-2">
                                <CustomizeTextField
                                    placeholder="e.g., T51, T74, UW3, E18"
                                    className="bg-gray-100 rounded-lg"
                                    value={exactProductCode}
                                    onChange={handleExactProductCodeChange}
                                    maxLength={10}
                                />
                                {errors.exactProductCode && (
                                    <p className="text-red-400 text-sm mt-1">{errors.exactProductCode}</p>
                                )}
                            </div>
                        )}

                        {productCodeType === "STARTING_WITH" && (
                            <div className="mt-2">
                                <div className="flex flex-wrap gap-3">
                                    {productCodeStartingWithOptions.map((char) => (
                                        <FormControlLabel
                                            key={char}
                                            control={
                                                <Checkbox
                                                    checked={productCodeStartingWith.includes(char)}
                                                    onChange={() => handleProductCodeStartingWithChange(char)}
                                                />
                                            }
                                            label={char}
                                        />
                                    ))}
                                </div>
                                {errors.productCodeStartingWith && (
                                    <p className="text-red-400 text-sm mt-1">{errors.productCodeStartingWith}</p>
                                )}
                            </div>
                        )}
                    </FormControl>

                    <FormControl fullWidth>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isNonNoType}
                                    onChange={(e) => {
                                        setIsNonNoType(e.target.checked);
                                        if (!e.target.checked) setTerminationAnswer("");
                                    }}
                                    className="text-purple-600"
                                />
                            }
                            label="Is this question Non-NO type"
                            className="mb-2"
                        />
                        {isNonNoType && (
                            <div className="ml-8 mb-4">
                                <label className="mb-1 text-sm font-semibold block">Select Termination Answer</label>
                                <Autocomplete
                                    options={answersStr.split("||").map(s => s.trim()).filter(Boolean)}
                                    value={terminationAnswer}
                                    onChange={(event, newValue) => setTerminationAnswer(newValue)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Select termination answer"
                                            className="bg-gray-100 rounded-lg border-none w-full"
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    "& fieldset": { border: "none" },
                                                }
                                            }}
                                        />
                                    )}
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    When the user selects this answer, the journey will stop immediately.
                                </p>
                            </div>
                        )}
                    </FormControl>

                    <FormControl fullWidth>
                        <label className="mb-1 text-sm font-semibold">Answers (|| separated)</label>
                        <CustomizeTextField
                            placeholder="Enter Answers (e.g. Yes || No)"
                            className="bg-gray-100 rounded-lg"
                            multiline
                            rows={3}
                            value={answersStr}
                            onChange={(e) => setAnswersStr(e.target.value)}
                        />
                    </FormControl>

                    <FormControl fullWidth>
                        <label className="mb-1 text-sm font-semibold">Language</label>
                        <Autocomplete
                            options={allLanguages}
                            value={language}
                            onChange={(event, newValue) => {
                                setLanguage(newValue);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Select language"
                                    className="bg-gray-100 rounded-lg border-none"
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            "& fieldset": { border: "none" },
                                        }
                                    }}
                                />
                            )}
                        />
                    </FormControl>

                    <button
                        onClick={handleSubmit}
                        className="w-full bg-purple-600 text-white py-2 rounded-lg font-bold hover:bg-purple-700 transition"
                    >
                        {isEdit ? "Update" : "Create"}
                    </button>
                </div>
            </Box>
        </Modal>
    );
};

const BulkQnaModal = ({ open, handleClose, refetch, templateId, availableLanguages = [] }) => {
    const customFetch = useAuthFetch();
    const [text, setText] = useState("");
    const productCodeStartingWithOptions = ["A", "D", "E", "G", "I", "M", "T", "U", "X", "Others"];

    useEffect(() => {
        if (open) {
            setText("");
        }
    }, [open]);

    const handleSubmit = async () => {
        if (!text.trim()) {
            toast.error("Please enter data");
            return;
        }

        const lines = text.split("\n").filter(line => line.trim());
        const items = [];
        const errors = [];
        const allowedLanguages = new Set(availableLanguages);
        lines.forEach((line, index) => {
            const parts = line.split("||").map(p => p.trim());
            if (parts.length < 2) {
                errors.push(`Line ${index + 1}: Invalid format (Question || Language)`);
            } else {
                const question = parts[0];
                const language = parts[1];
                const productCodeRaw = parts[2] || "";
                const terminationAnswerRaw = parts[3] || "";
                const answers = parts.slice(4).filter(Boolean);

                // Handle productCode: "-" or "NA" or empty string means no product code
                const productCodeValue = (productCodeRaw === "-" || productCodeRaw.toUpperCase() === "NA" || !productCodeRaw.trim()) ? null : productCodeRaw.trim().toUpperCase();

                // Determine productCodeType based on value
                // If it's a single alphanumeric code (1-10 chars), treat as EXACT
                // If it contains commas or multiple characters, treat as STARTING_WITH
                let productCodeType = null;
                let finalProductCodeValue = null;

                if (productCodeValue) {
                    if (/^[A-Z0-9]{1,10}$/.test(productCodeValue)) {
                        productCodeType = "EXACT";
                        finalProductCodeValue = productCodeValue;
                    } else if (productCodeValue.includes(',')) {
                        productCodeType = "STARTING_WITH";
                        finalProductCodeValue = productCodeValue;
                    } else {
                        // Try to parse as starting with character
                        const chars = productCodeValue.split(',').map(c => c.trim().toUpperCase()).filter(Boolean);
                        const validStartingChars = ["A", "D", "E", "G", "I", "M", "T", "U", "X", "OTHERS"];
                        if (chars.every(c => validStartingChars.includes(c))) {
                            productCodeType = "STARTING_WITH";
                            finalProductCodeValue = chars.join(',');
                        } else {
                            // Default to EXACT if can't determine
                            productCodeType = "EXACT";
                            finalProductCodeValue = productCodeValue;
                        }
                    }
                }

                if (question && language) {
                    if (!allowedLanguages.has(language)) {
                        errors.push(`Line ${index + 1}: Invalid language '${language}'. Allowed: ${availableLanguages.join(", ")}`);
                    } else {
                        // Handle terminationAnswer: "-" or empty means no termination answer
                        const terminationAnswer = (terminationAnswerRaw === "-" || !terminationAnswerRaw.trim()) ? null : terminationAnswerRaw.trim();
                        const isNonNoType = !!terminationAnswer;

                        items.push({
                            templateId,
                            question,
                            language,
                            productCodeType,
                            productCodeValue: finalProductCodeValue,
                            isNonNoType,
                            terminationAnswer,
                            answers
                        });
                    }
                } else {
                    errors.push(`Line ${index + 1}: Missing question or language`);
                }
            }
        });

        if (errors.length > 0) {
            toast.error(errors[0]); // Show first error
            return;
        }
        console.log("items ", items);

        if (items.length === 0) {
            toast.error("No valid items to add");
            return;
        }

        const url = new URL(
            `${process.env.REACT_APP_API_URL}/v1/dashboard/qnaAssociation/bulkUpsert`
        );

        const body = {
            items
        };

        const { response, status, message } = await customFetch(url, "POST", body);

        if (status === 200 || status === 201) {
            toast.success("Bulk QnA Created Successfully!");
            handleClose();
            refetch();
        } else {
            toast.error(message || "Something went wrong!");
        }
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <Box sx={style}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">Bulk Add Questions</h2>
                    <button onClick={handleClose}><CloseOutlined /></button>
                </div>

                <div className="space-y-4">
                    <FormControl fullWidth>
                        <label className="mb-1 text-sm font-semibold">
                            Enter Question || Language || ProductCode || TerminationAnswer || Answers... (one per line)
                        </label>
                        <CustomizeTextField
                            placeholder={`Example:\nDo you agree? || English || T51 || Disagree || Agree || Disagree\nWhat is your name? || English || - || - || YES || NO\nAapka naam kya hai? || Hindi || T,U,X || NAHI || HAAN || NAHI`}
                            className="bg-gray-100 rounded-lg"
                            multiline
                            rows={10}
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                        />
                    </FormControl>

                    <button
                        onClick={handleSubmit}
                        className="w-full bg-purple-600 text-white py-2 rounded-lg font-bold hover:bg-purple-700 transition"
                    >
                        Bulk Create
                    </button>
                </div>
            </Box>
        </Modal>
    );
};

const QuestionList = () => {
    const { templateId } = useParams();
    const navigate = useNavigate();
    const customFetch = useAuthFetch();

    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
    const [editData, setEditData] = useState(null);

    const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [availableLanguages, setAvailableLanguages] = useState([]);
    const [lastUsedLanguage, setLastUsedLanguage] = useState(availableLanguages.at(0) || "");
    const [searchParams] = useSearchParams();
    const templateName = searchParams.get("name") || "";

    const [filterObject, setFilterObject] = useState([
        {
            label: "Product Code",
            searchFilter: "productCode",
            value: null,
        },
        {
            label: "Language",
            searchFilter: "language",
            value: null,
        },
        {
            searchFilter: "start",
            value: null,
        },
        {
            searchFilter: "end",
            value: null,
        },
    ]);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const url = new URL(`${process.env.REACT_APP_API_URL}/v1/dashboard/qnaAssociation/fetchAll`);
            url.searchParams.append("templateId", templateId);
            url.searchParams.append("page", page + 1);
            url.searchParams.append("perPage", rowsPerPage);

            filterObject.forEach((item) => {
                if (item.value !== null && item.value !== undefined && item.value !== "") {
                    url.searchParams.append(item.searchFilter, item.value);
                }
            });

            const { response, status } = await customFetch(url, "GET");

            if (status === 200) {
                console.log("RES qna ", response)
                setData(response);
                if (response?.languages) {
                    setAvailableLanguages(response.languages);
                }
            } else {
                console.error("Error fetching QnA");
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [templateId, page, rowsPerPage, filterObject]);

    const handleDelete = async () => {
        console.log("deleteId ", deleteId);

        if (!deleteId) return;
        const url = new URL(`${process.env.REACT_APP_API_URL}/v1/dashboard/qnaAssociation/delete`);
        const { status, message } = await customFetch(url, "POST", { id: deleteId });

        if (status === 200) {
            toast.success("Question deleted successfully");
            fetchData();
        } else {
            toast.error(message || "Failed to delete question");
        }
        setOpenDeleteConfirm(false);
        setDeleteId(null);
    };

    const columns = ["Question", "Language", "Product Code", "Actions"];

    // Format product code display for a question (same format as QuestionTemplateList)
    const formatProductCode = (productCodeType, productCodeValue) => {
        if (!productCodeType || !productCodeValue) {
            return "-";
        }

        if (productCodeType === "EXACT") {
            return `Exact: ${productCodeValue}`;
        } else if (productCodeType === "STARTING_WITH") {
            const chars = productCodeValue.split(",").map(c => c.trim()).filter(Boolean);
            return `Starting With: ${chars.join(", ")}`;
        }

        return "-";
    };

    return (
        <div className="py-1 px-4" style={{ height: `calc(100vh - 100px)` }}>
            <div className="flex flex-row mb-4 items-center justify-between">
                <div className="flex items-center">
                    <button onClick={() => navigate(-1)} className="mr-2 p-1 hover:bg-gray-200 rounded-full">
                        <ArrowBack />
                    </button>
                    <p className="text-xl mb-0 font-extrabold">
                        Questions for Template: {templateName || templateId} ({(data?.pageInfo?.total || 0)})
                    </p>
                </div>

                <div className="flex gap-2">
                    <button
                        onClick={fetchData}
                        className="border hover:bg-gray-50 border-[#E0E0E0] mx-2 rounded py-1.5 px-3"
                    >
                        Refresh Data
                    </button>
                    <FilterDropdown object={filterObject} setObject={setFilterObject} />
                    <button
                        onClick={() => setIsBulkModalOpen(true)}
                        className="bg-blue-600 text-white flex items-center py-2 px-4 font-bold rounded hover:bg-blue-700"
                    >
                        <Add fontSize="small" className="mr-1" /> Bulk Add
                    </button>
                    <button
                        onClick={() => { setEditData(null); setIsModalOpen(true); }}
                        className="bg-purple-600 text-white flex items-center py-2 px-4 font-bold rounded hover:bg-purple-700"
                    >
                        <Add fontSize="small" className="mr-1" /> Create Question
                    </button>
                </div>
            </div>

            {!data && !isLoading ? (
                <NoDataTable columns={columns} message="Server Error" />
            ) : data?.data?.length === 0 && !isLoading ? (
                <NoDataTable columns={columns} message="No questions found for this template." />
            ) : (
                <CustomTable>
                    <TableHeader columns={columns} />
                    <MainTableBody isLoading={isLoading}>
                        {isLoading
                            ? [1, 2, 3, 4, 5].map((i) => <LoadingTableSkeleton key={i} index={i} cols={columns.length} />)
                            : data?.data?.map((row, i) => (
                                <CustomTableRow
                                    key={i}
                                    columns={[
                                        { text: row.question, tooltipText: row.question, renderToolTip: true },
                                        { text: row.language || "-" },
                                        { text: formatProductCode(row.productCodeType, row.productCodeValue) },
                                        {
                                            actions: [
                                                () => (
                                                    <button
                                                        onClick={() => { setEditData(row); setIsModalOpen(true); }}
                                                        className="text-blue-600 hover:text-blue-800"
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </button>
                                                ),
                                                () => (
                                                    <button
                                                        onClick={() => { console.log("row", row); setDeleteId(row.id); setOpenDeleteConfirm(true); }}
                                                        className="text-red-500 hover:text-red-700"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </button>
                                                )
                                            ]
                                        }
                                    ]}
                                />
                            ))
                        }
                    </MainTableBody>
                    {data?.pageInfo && (
                        <TablePagination
                            className="sticky bottom-0 bg-white"
                            component="div"
                            count={data.pageInfo.total}
                            page={page}
                            onPageChange={(e, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                        />
                    )}
                </CustomTable>
            )}

            <QnaModal
                open={isModalOpen}
                handleClose={() => setIsModalOpen(false)}
                refetch={fetchData}
                initialData={editData}
                isEdit={!!editData}
                templateId={templateId}
                availableLanguages={availableLanguages}
                defaultLanguage={lastUsedLanguage}
                onLanguageChange={setLastUsedLanguage}
            />

            <BulkQnaModal
                open={isBulkModalOpen}
                handleClose={() => setIsBulkModalOpen(false)}
                refetch={fetchData}
                templateId={templateId}
                availableLanguages={availableLanguages}
            />

            <ConfirmBox
                open={openDeleteConfirm}
                setOpen={() => setOpenDeleteConfirm(false)}
                buttonText="Delete"
                buttonHandler={handleDelete}
                text="Are you sure you want to delete this question?"
                extraButtonText="Cancel"
                extraButtonHandler={() => setOpenDeleteConfirm(false)}
            />
        </div>
    );
};

export default QuestionList;
